import React, { useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Plus, X, ChevronUp, ChevronDown } from 'lucide-react';
import QueBuilder from './QueBuilder';
import { useBusiness } from '../contexts/BusinessContext';
import { usePermissions } from '../contexts/PermissionContext';
import { useSupabase } from '../contexts/SupabaseContext';
import { Customer } from '../lib/customers';
import { toast } from 'sonner';
import SelectCustomerModal from '../components/manage/SelectCustomerModal';
import QueueReorderModal from '../components/QueueReorderModal';
import { triggerBookingAutomations } from '../lib/automationsV2';
import { getBooking } from '../lib/bookings';
import { Edit2 } from 'lucide-react';

type QueTab = 'que' | 'builder';

const tabs: { id: QueTab; label: string }[] = [
  { id: 'que', label: 'Que' },
  { id: 'builder', label: 'Builder' },
];

export default function Que() {
  const [activeTab, setActiveTab] = useState<QueTab>('que');
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const { currentResource, permissions } = usePermissions();
  const [queueEntries, setQueueEntries] = useState<any[]>([]);
  const [isLoadingQueue, setIsLoadingQueue] = useState(false);
  const [queueError, setQueueError] = useState<string | null>(null);
  const [isQueueTableMissing, setIsQueueTableMissing] = useState(false);
  const [handoffStages, setHandoffStages] = useState<any[]>([]);
  const [selectedStageId, setSelectedStageId] = useState<string | null>(null);
  const [hasLoadedResourceDefault, setHasLoadedResourceDefault] = useState(false);
  const [stageEntries, setStageEntries] = useState<any[]>([]);
  const [isLoadingStages, setIsLoadingStages] = useState(false);
  const [isLoadingStageEntries, setIsLoadingStageEntries] = useState(false);
  const [newBookingIds, setNewBookingIds] = useState<Set<string>>(new Set());
  const [bookingSourceStage, setBookingSourceStage] = useState<Map<string, string>>(new Map());
  const previousStageEntriesRef = useRef<Set<string>>(new Set());
  const previousAllStageEntriesRef = useRef<Map<string, string>>(new Map()); // Maps booking_id -> stage_id
  const hasInitializedStageEntriesRef = useRef(false);
  const hasInitializedAllStageEntriesRef = useRef(false);
  const [isQueueModalOpen, setIsQueueModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isAddModalAnimating, setIsAddModalAnimating] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedService, setSelectedService] = useState<{ service: any; priceOption: any } | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isSelectCustomerModalOpen, setIsSelectCustomerModalOpen] = useState(false);
  const [isPullingFromQueue, setIsPullingFromQueue] = useState(false);
  const [vehicleYear, setVehicleYear] = useState('');
  const [vehicleMake, setVehicleMake] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [vehicleColor, setVehicleColor] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [taxRates, setTaxRates] = useState<any[]>([]);
  const [isReordering, setIsReordering] = useState(false);
  const [isReorderModalOpen, setIsReorderModalOpen] = useState(false);

  // Helper function to get border color based on booking status
  const getStatusBorderColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'border-blue-400 border-l-4';
      case 'in_progress':
        return 'border-yellow-400 border-l-4';
      case 'completed':
        return 'border-green-400 border-l-4';
      default:
        return 'border-gray-200';
    }
  };

  // Helper function to get previous stage name based on current stage order
  const getPreviousStageName = (currentStageOrder: number) => {
    if (currentStageOrder <= 1) return 'Queue';
    const previousStage = handoffStages.find(s => s.stage_order === currentStageOrder - 1);
    return previousStage?.title || 'Previous Stage';
  };

  const fetchQueueEntries = async (skipLoadingState = false) => {
    if (!selectedBusiness?.id) return;
    if (!skipLoadingState) setIsLoadingQueue(true);
    setQueueError(null);
    setIsQueueTableMissing(false);
    try {
      const { data, error } = await supabase
        .from('queue_entries')
        .select(`
          id,
          booking_id,
          status,
          priority,
          queue_position,
          queued_at,
          booking:booking_id(
            id,
            created_at,
            status,
            start_date,
            start_time,
            notes,
            total_amount,
            customer:customers!bookings_customer_id_fkey(
              first_name,
              last_name,
              phone,
              email
            ),
            services:booking_services(
              id,
              name,
              price,
              duration_minutes,
              vehicle:customer_vehicles(
                make,
                model,
                year,
                color
              )
            ),
            payments(
              id,
              amount,
              status
            )
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('status', 'waiting')
        .order('queue_position', { ascending: true });

      if (error) {
        const message = (error as any)?.message || '';
        if (message.toLowerCase().includes('relation "queue_entries" does not exist')) {
          setIsQueueTableMissing(true);
          setQueueEntries([]);
          return;
        }
        throw error;
      }
      
      // Only update if data actually changed (prevents unnecessary re-renders and scroll resets)
      const newData = data || [];
      setQueueEntries(prev => {
        const prevJson = JSON.stringify(prev);
        const newJson = JSON.stringify(newData);
        if (prevJson === newJson) {
          return prev; // No change, keep same reference to prevent re-render
        }
        return newData;
      });
    } catch (err) {
      console.error('Error loading queue entries:', err);
      const message = (err as any)?.message || 'Failed to load queue';
      setQueueError(message);
    } finally {
      if (!skipLoadingState) setIsLoadingQueue(false);
    }
  };

  useEffect(() => {
    fetchQueueEntries();
  }, [selectedBusiness?.id]);

  const fetchCategories = async () => {
    if (!selectedBusiness?.id) return;
    setIsLoadingCategories(true);
    try {
      const { data, error } = await supabase
        .from('que_intake_flow')
        .select('id, category_title, services')
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error loading queue categories:', error);
      toast.error('Failed to load queue categories');
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const fetchTaxRates = async () => {
    if (!selectedBusiness?.id) return;
    try {
      const { data, error } = await supabase
        .from('tax_rates')
        .select('id, name, rate')
        .eq('business_id', selectedBusiness.id);

      if (error) throw error;
      setTaxRates(data || []);
    } catch (error) {
      console.error('Error loading tax rates:', error);
    }
  };

  useEffect(() => {
    if (isAddModalOpen) {
      setIsAddModalAnimating(true);
      fetchCategories();
      fetchTaxRates();
    }
  }, [isAddModalOpen, selectedBusiness?.id]);

  const handleAddModalTransitionEnd = (e: React.TransitionEvent) => {
    // Only handle transition end for opacity changes on the backdrop
    // This ensures we only clean up after the fade-out animation completes
    if (e.target === e.currentTarget && !isAddModalOpen) {
      setIsAddModalAnimating(false);
      // Reset form data
      setSelectedCategory(null);
      setSelectedService(null);
      setSelectedCustomer(null);
      setVehicleYear('');
      setVehicleMake('');
      setVehicleModel('');
      setVehicleColor('');
      setNotes('');
    }
  };

  const handleReorderQueue = async (entryId: string, direction: 'up' | 'down') => {
    if (!selectedBusiness?.id || isReordering) return;

    const currentEntry = queueEntries.find(e => e.id === entryId);
    if (!currentEntry) return;

    const currentPosition = currentEntry.queue_position;
    let targetPosition: number;

    if (direction === 'up') {
      if (currentPosition === 1) return; // Already at top
      targetPosition = currentPosition - 1;
    } else {
      if (currentPosition === queueEntries.length) return; // Already at bottom
      targetPosition = currentPosition + 1;
    }

    const targetEntry = queueEntries.find(e => e.queue_position === targetPosition);
    if (!targetEntry) return;

    setIsReordering(true);
    try {
      // Swap positions: update both entries in a transaction-like manner
      const updates = [
        { id: entryId, queue_position: targetPosition },
        { id: targetEntry.id, queue_position: currentPosition }
      ];

      // Update both entries
      const updatePromises = updates.map(update =>
        supabase
          .from('queue_entries')
          .update({ queue_position: update.queue_position, updated_at: new Date().toISOString() })
          .eq('id', update.id)
      );

      const results = await Promise.all(updatePromises);
      
      const hasError = results.some(result => result.error);
      if (hasError) {
        throw new Error('Failed to update queue positions');
      }

      // Refresh queue entries
      await fetchQueueEntries(true);
      toast.success(`Queue position updated`);
    } catch (err) {
      console.error('Error reordering queue:', err);
      toast.error('Failed to reorder queue. Please try again.');
    } finally {
      setIsReordering(false);
    }
  };

  // Fallback: ensure cleanup happens even if transition end doesn't fire
  useEffect(() => {
    if (!isAddModalOpen && isAddModalAnimating) {
      const timeout = setTimeout(() => {
        setIsAddModalAnimating(false);
      }, 300); // Slightly longer than the transition duration (200ms)
      return () => clearTimeout(timeout);
    }
  }, [isAddModalOpen, isAddModalAnimating]);

  const handleAddToQueue = async () => {
    if (!selectedBusiness?.id || !selectedService) {
      toast.error('Please select a service');
      return;
    }
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }
    if (!vehicleYear || !vehicleMake || !vehicleModel || !vehicleColor) {
      toast.error('Please fill in all vehicle fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const customerId = selectedCustomer.id;

      const { data: vehicleData, error: vehicleError } = await supabase
        .from('customer_vehicles')
        .insert({
          customer_id: customerId,
          make: vehicleMake,
          model: vehicleModel,
          year: vehicleYear,
          color: vehicleColor
        })
        .select('id')
        .single();

      if (vehicleError) throw vehicleError;
      if (!vehicleData?.id) throw new Error('Unable to create vehicle record');

      const servicePrice = Number(selectedService.priceOption?.price || 0);
      const taxRateTotal = taxRates.reduce((sum, rate) => sum + (Number(rate.rate) || 0), 0);
      const taxAmount = Math.round((servicePrice * taxRateTotal / 100) * 100) / 100;
      const grandTotal = Math.round((servicePrice + taxAmount) * 100) / 100;

      const { data: bookingData, error: bookingError } = await supabase
        .from('bookings')
        .insert({
          business_id: selectedBusiness.id,
          customer_id: customerId,
          status: 'scheduled',
          notes: notes || 'Created via manual queue entry',
          subtotal: servicePrice,
          tax_amount: selectedService.service?.tax_exempt ? 0 : taxAmount,
          total_amount: selectedService.service?.tax_exempt ? servicePrice : grandTotal,
          created_by: 'manual',
          in_que: true,
          que_step_id: null,
          is_deleted: false
        })
        .select('id')
        .single();

      if (bookingError) throw bookingError;
      if (!bookingData?.id) throw new Error('Unable to create booking');

      const serviceTaxRate = selectedService.service?.tax_exempt ? 0 : taxRateTotal;
      const serviceTaxAmount = selectedService.service?.tax_exempt ? 0 : taxAmount;
      const serviceTotal = selectedService.service?.tax_exempt ? servicePrice : grandTotal;

      const { error: serviceError } = await supabase
        .from('booking_services')
        .insert({
          business_id: selectedBusiness.id,
          booking_id: bookingData.id,
          service_id: selectedService.service?.id,
          vehicle_id: vehicleData.id,
          name: selectedService.service?.name || 'Service',
          description: selectedService.service?.description || '',
          price: servicePrice,
          tax_exempt: selectedService.service?.tax_exempt || false,
          tax_rate: serviceTaxRate,
          tax_amount: serviceTaxAmount,
          total_amount: serviceTotal,
          duration_minutes: selectedService.priceOption?.duration_minutes || 0,
          notes: notes || null
        });

      if (serviceError) throw serviceError;

      toast.success('Vehicle added to queue successfully');
      
      // Reset form
      setFirstName('');
      setLastName('');
      setVehicleYear('');
      setVehicleMake('');
      setVehicleModel('');
      setVehicleColor('');
      setEmail('');
      setPhone('');
      setNotes('');
      setSelectedService(null);
      setSelectedCategory(null);
      setIsAddModalOpen(false);
      // Don't set isAddModalAnimating to false here - let the transition end handler do it
      
      // Refresh queue
      await fetchQueueEntries();
    } catch (err) {
      console.error('Error adding to queue:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to add vehicle to queue');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePullFromQueue = async () => {
    if (!selectedBusiness?.id || !selectedStageId) return;
    
    setIsPullingFromQueue(true);
    try {
      // Get the selected stage info
      const stage = handoffStages.find(s => s.id === selectedStageId);
      if (!stage || stage.stage_order !== 1) {
        toast.error('Pull from queue is only available for Stage 1');
        return;
      }

      console.log('🔵 Attempting manual pull from queue...', {
        businessId: selectedBusiness.id,
        stageId: selectedStageId,
        stageName: stage.title,
        currentStageEntries: stageEntries.length,
        queueEntries: queueEntries.length
      });

      // Use the dedicated manual pull function
      const { data, error } = await supabase.rpc('manual_pull_from_queue', {
        p_business_id: selectedBusiness.id,
        p_stage_id: selectedStageId
      });

      if (error) {
        console.error('❌ Manual pull error:', error);
        throw error;
      }

      console.log('✅ Manual pull result:', data);

      if (data && typeof data === 'object' && 'success' in data) {
        if (data.success) {
          toast.success('Successfully pulled booking from queue');
        } else {
          toast.error(data.error || 'Failed to pull from queue');
          return;
        }
      } else {
        toast.success('Pulled booking from queue');
      }
      
      // Refresh UI
      await Promise.all([
        fetchQueueEntries(true),
        fetchStageEntries(selectedStageId)
      ]);
    } catch (err) {
      console.error('❌ Error pulling from queue:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to pull from queue');
    } finally {
      setIsPullingFromQueue(false);
    }
  };

  const selectedCategoryData = categories.find(category => category.id === selectedCategory);
  const allServices = categories.flatMap(category => category.services || []);
  const servicesToShow = selectedCategory
    ? (selectedCategoryData?.services || [])
    : allServices;

  // Initial frontend fallback safety net
  useEffect(() => {
    if (!selectedBusiness?.id) return;

    const checkAndProcessHandoff = async () => {
      try {
        // Check if there are queue entries and if handoff is enabled
        const { data: settings } = await supabase
          .from('que_settings')
          .select('advance_to_handoff')
          .eq('business_id', selectedBusiness.id)
          .single();

        if (!settings?.advance_to_handoff) return;

        // Check if there are waiting queue entries or completed stage entries
        const [queueData, stageData] = await Promise.all([
          supabase
            .from('queue_entries')
            .select('id')
            .eq('business_id', selectedBusiness.id)
            .eq('status', 'waiting')
            .limit(1),
          supabase
            .from('queue_handoff_entries')
            .select('id')
            .eq('business_id', selectedBusiness.id)
            .eq('status', 'completed')
            .limit(1)
        ]);

        if ((queueData.data && queueData.data.length > 0) || 
            (stageData.data && stageData.data.length > 0)) {
          // There are entries that need processing - manually invoke the handoff processor as fallback
          await supabase.rpc('process_queue_handoff', {
            p_business_id: selectedBusiness.id
          });
          
          // Refresh the UI (skip loading state for background updates)
          fetchQueueEntries(true);
          if (selectedStageId) {
            fetchStageEntries(selectedStageId, true);
          } else {
            fetchAllStageEntries(true);
          }
        }
      } catch (err) {
        console.error('Fallback handoff check failed:', err);
      }
    };

    // Check immediately on mount
    checkAndProcessHandoff();

    // Then check every 15 seconds as a fallback safety net (reduced from 5s to prevent excessive updates)
    const interval = setInterval(checkAndProcessHandoff, 15000);

    return () => clearInterval(interval);
  }, [selectedBusiness?.id, selectedStageId]);

  const fetchHandoffStages = async () => {
    if (!selectedBusiness?.id) return;
    setIsLoadingStages(true);
    try {
      const { data, error } = await supabase
        .from('queue_handoff_stages')
        .select('id, title, stage_order, max_bookings, handoff_mode')
        .eq('business_id', selectedBusiness.id)
        .order('stage_order', { ascending: true });

      if (error) throw error;
      
      setHandoffStages(data || []);
      // Don't auto-select any stage - keep default as "View all stages" unless resource preference exists
    } catch (err) {
      console.error('Error loading handoff stages:', err);
    } finally {
      setIsLoadingStages(false);
    }
  };

  const fetchStageEntries = async (stageId: string | null, skipLoadingState = false) => {
    if (!selectedBusiness?.id || !stageId) {
      setStageEntries([]);
      return;
    }
    if (!skipLoadingState) setIsLoadingStageEntries(true);
    try {
      const { data, error } = await supabase
        .from('queue_handoff_entries')
        .select(`
          id,
          booking_id,
          stage_id,
          status,
          entered_at,
          stage:queue_handoff_stages(
            id,
            title,
            stage_order,
            handoff_mode
          ),
          booking:booking_id(
            id,
            created_at,
            status,
            start_date,
            start_time,
            notes,
            total_amount,
            customer:customers!bookings_customer_id_fkey(
              first_name,
              last_name,
              phone,
              email
            ),
            services:booking_services(
              id,
              name,
              price,
              duration_minutes,
              vehicle:customer_vehicles(
                make,
                model,
                year,
                color
              )
            ),
            payments(
              id,
              amount,
              status
            )
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('stage_id', stageId)
        .in('status', ['active', 'completed'])
        .order('entered_at', { ascending: true });

      if (error) throw error;
      
      // Only update if data actually changed (prevents scroll resets)
      const newData = data || [];
      setStageEntries(prev => {
        const prevJson = JSON.stringify(prev);
        const newJson = JSON.stringify(newData);
        if (prevJson === newJson) {
          return prev; // No change, keep same reference
        }
        
        // Detect new bookings that just entered this stage
        const currentBookingIds = new Set(newData.map((entry: any) => entry.booking_id));
        const previousBookingIds = previousStageEntriesRef.current;
        
        // On first load, just initialize the ref without showing badges
        if (!hasInitializedStageEntriesRef.current) {
          hasInitializedStageEntriesRef.current = true;
          previousStageEntriesRef.current = currentBookingIds;
          return newData;
        }
        
        const newlyAddedBookingIds = new Set(
          [...currentBookingIds].filter(id => !previousBookingIds.has(id))
        );
        
        // Mark these bookings as "new" and determine their source
        if (newlyAddedBookingIds.size > 0) {
          setNewBookingIds(prev => new Set([...prev, ...newlyAddedBookingIds]));
          
          // Determine source stage for each new booking
          const currentStageOrder = newData[0]?.stage?.stage_order;
          const sourceStageName = currentStageOrder === 1 ? 'Queue' : getPreviousStageName(currentStageOrder);
          
          setBookingSourceStage(prev => {
            const updated = new Map(prev);
            newlyAddedBookingIds.forEach(bookingId => {
              updated.set(bookingId, sourceStageName);
            });
            return updated;
          });
          
          newlyAddedBookingIds.forEach(bookingId => {
            setTimeout(() => {
              setNewBookingIds(prev => {
                const updated = new Set(prev);
                updated.delete(bookingId);
                return updated;
              });
              setBookingSourceStage(prev => {
                const updated = new Map(prev);
                updated.delete(bookingId);
                return updated;
              });
            }, 5000);
          });
        }
        
        // Update the ref for next comparison
        previousStageEntriesRef.current = currentBookingIds;
        
        return newData;
      });
    } catch (err) {
      console.error('Error loading stage entries:', err);
      setStageEntries([]);
    } finally {
      if (!skipLoadingState) setIsLoadingStageEntries(false);
    }
  };

  useEffect(() => {
    fetchHandoffStages();
  }, [selectedBusiness?.id]);

  useEffect(() => {
    const loadResourceDefaultStage = async () => {
      if (!selectedBusiness?.id || !currentResource?.id || hasLoadedResourceDefault) return;
      
      try {
        const { data, error } = await supabase
          .from('queue_resource_defaults')
          .select('default_stage_id')
          .eq('resource_id', currentResource.id)
          .maybeSingle();

        if (error) throw error;

        if (data?.default_stage_id) {
          setSelectedStageId(data.default_stage_id);
        } else {
          setSelectedStageId(null);
        }
      } catch (err) {
        console.error('Error loading queue default stage:', err);
      } finally {
        setHasLoadedResourceDefault(true);
      }
    };

    loadResourceDefaultStage();
  }, [selectedBusiness?.id, currentResource?.id, hasLoadedResourceDefault, supabase]);

  useEffect(() => {
    // If selectedStageId is null, we're viewing all stages
    if (selectedStageId === null) {
      fetchAllStageEntries();
      hasInitializedAllStageEntriesRef.current = false;
      hasInitializedStageEntriesRef.current = false;
    } else {
      // Otherwise, fetch entries for the specific stage
      fetchStageEntries(selectedStageId);
      hasInitializedStageEntriesRef.current = false;
      hasInitializedAllStageEntriesRef.current = false;
    }
  }, [selectedStageId, selectedBusiness?.id]);

  const fetchAllStageEntries = async (skipLoadingState = false) => {
    if (!selectedBusiness?.id) return;
    if (!skipLoadingState) setIsLoadingStageEntries(true);
    try {
      const { data, error } = await supabase
        .from('queue_handoff_entries')
        .select(`
          id,
          booking_id,
          stage_id,
          status,
          entered_at,
          stage:queue_handoff_stages(
            id,
            title,
            stage_order,
            handoff_mode
          ),
          booking:booking_id(
            id,
            created_at,
            status,
            start_date,
            start_time,
            notes,
            total_amount,
            customer:customers!bookings_customer_id_fkey(
              first_name,
              last_name,
              phone,
              email
            ),
            services:booking_services(
              id,
              name,
              price,
              duration_minutes,
              vehicle:customer_vehicles(
                make,
                model,
                year,
                color
              )
            ),
            payments(
              id,
              amount,
              status
            )
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .in('status', ['active', 'completed'])
        .order('entered_at', { ascending: true });

      if (error) throw error;
      
      // Only update if data actually changed (prevents scroll resets)
      const newData = data || [];
      setStageEntries(prev => {
        const prevJson = JSON.stringify(prev);
        const newJson = JSON.stringify(newData);
        if (prevJson === newJson) {
          return prev; // No change, keep same reference
        }
        
        // Track booking positions: Map booking_id -> stage_id
        const currentBookingStageMap = new Map(
          newData.map((entry: any) => [entry.booking_id, entry.stage_id])
        );
        const previousBookingStageMap = previousAllStageEntriesRef.current;
        
        // On first load, just initialize the ref without showing badges
        if (!hasInitializedAllStageEntriesRef.current) {
          hasInitializedAllStageEntriesRef.current = true;
          previousAllStageEntriesRef.current = currentBookingStageMap;
          return newData;
        }
        
        // Detect bookings that moved to a new stage
        const newlyMovedBookings: Array<{ bookingId: string; currentStageId: string; currentStageOrder: number }> = [];
        
        currentBookingStageMap.forEach((currentStageId, bookingId) => {
          const previousStageId = previousBookingStageMap.get(bookingId);
          
          // If booking wasn't in any stage before, or moved to a different stage
          if (!previousStageId || previousStageId !== currentStageId) {
            const entry = newData.find((e: any) => e.booking_id === bookingId);
            if (entry?.stage?.stage_order) {
              newlyMovedBookings.push({
                bookingId,
                currentStageId,
                currentStageOrder: entry.stage.stage_order
              });
            }
          }
        });
        
        // Mark new bookings and determine their source
        if (newlyMovedBookings.length > 0) {
          const newBookingIdsSet = new Set(newlyMovedBookings.map(b => b.bookingId));
          setNewBookingIds(prev => new Set([...prev, ...newBookingIdsSet]));
          
          setBookingSourceStage(prev => {
            const updated = new Map(prev);
            newlyMovedBookings.forEach(({ bookingId, currentStageOrder }) => {
              const sourceStageName = currentStageOrder === 1 ? 'Queue' : getPreviousStageName(currentStageOrder);
              updated.set(bookingId, sourceStageName);
            });
            return updated;
          });
          
          newlyMovedBookings.forEach(({ bookingId }) => {
            setTimeout(() => {
              setNewBookingIds(prev => {
                const updated = new Set(prev);
                updated.delete(bookingId);
                return updated;
              });
              setBookingSourceStage(prev => {
                const updated = new Map(prev);
                updated.delete(bookingId);
                return updated;
              });
            }, 5000);
          });
        }
        
        // Update the ref for next comparison
        previousAllStageEntriesRef.current = currentBookingStageMap;
        
        return newData;
      });
    } catch (err) {
      console.error('Error loading stage entries:', err);
      setStageEntries([]);
    } finally {
      if (!skipLoadingState) setIsLoadingStageEntries(false);
    }
  };

  // Force advance a stuck completed entry to the next stage
  const handleForceAdvance = async (entryId: string) => {
    try {
      const { data, error } = await supabase.rpc('advance_completed_entry', {
        p_entry_id: entryId
      });

      if (error) throw error;

      if (data?.success) {
        // Refresh the UI
        if (selectedStageId) {
          await fetchStageEntries(selectedStageId);
        } else {
          await fetchAllStageEntries();
        }
        await fetchQueueEntries();
      } else {
        console.error('Force advance failed:', data?.error);
        alert(data?.error || 'Failed to advance entry');
      }
    } catch (err) {
      console.error('Error forcing advance:', err);
    }
  };

  const handleMarkStageCompleted = async (entryId: string) => {
    try {
      // First, get the entry details to know the booking_id and stage_id
      const { data: entryData, error: fetchError } = await supabase
        .from('queue_handoff_entries')
        .select('id, booking_id, stage_id')
        .eq('id', entryId)
        .single();

      if (fetchError) throw fetchError;

      const bookingId = entryData?.booking_id;
      const stageId = entryData?.stage_id;

      // Check if this is the final stage
      let isFinalStage = false;
      if (stageId && handoffStages.length > 0) {
        const sortedStages = [...handoffStages].sort((a, b) => a.stage_order - b.stage_order);
        const finalStage = sortedStages[sortedStages.length - 1];
        isFinalStage = finalStage?.id === stageId;
      }

      const { error } = await supabase
        .from('queue_handoff_entries')
        .update({ status: 'completed' })
        .eq('id', entryId);

      if (error) throw error;

      setStageEntries(prev =>
        prev.map(entry => entry.id === entryId ? { ...entry, status: 'completed' } : entry)
      );

      if (selectedBusiness?.id) {
        // Call process_queue_handoff multiple times with delays to ensure the entire chain advances
        // This is needed because each call may only process one advancement
        await supabase.rpc('process_queue_handoff', {
          p_business_id: selectedBusiness.id
        });
        
        // Second call after short delay to catch any additional movements
        await new Promise(resolve => setTimeout(resolve, 300));
        await supabase.rpc('process_queue_handoff', {
          p_business_id: selectedBusiness.id
        });
        
        // Third call for safety (e.g., if multiple stages need to cascade)
        await new Promise(resolve => setTimeout(resolve, 300));
        await supabase.rpc('process_queue_handoff', {
          p_business_id: selectedBusiness.id
        });

        // If this was the final stage, trigger the booking completed automation
        if (isFinalStage && bookingId) {
          try {
            console.log('🎯 Final stage completed, triggering booking completed automation for booking:', bookingId);
            console.log('🎯 isFinalStage:', isFinalStage, 'stageId:', stageId);
            console.log('🎯 handoffStages:', handoffStages.map(s => ({ id: s.id, order: s.stage_order, title: s.title })));
            
            // Get the updated booking data
            const updatedBooking = await getBooking(bookingId);
            console.log('🎯 Got booking data:', updatedBooking ? 'yes' : 'no');
            
            if (updatedBooking) {
              // Ensure booking_id is set (triggerBookingAutomations expects this field)
              const bookingDataForAutomation = {
                ...updatedBooking,
                booking_id: updatedBooking.id, // Ensure booking_id is set
                schedule: {
                  start_date: updatedBooking.start_date,
                  start_time: updatedBooking.start_time,
                  end_date: updatedBooking.end_date,
                  end_time: updatedBooking.end_time,
                  drop_off_date: updatedBooking.drop_off_date,
                  drop_off_time: updatedBooking.drop_off_time
                }
              };
              
              console.log('🎯 Calling triggerBookingAutomations with:', {
                businessId: selectedBusiness.id,
                eventType: 'completed',
                bookingId: bookingDataForAutomation.booking_id,
                customerPhone: bookingDataForAutomation.customer?.phone
              });
              
              // Trigger the booking completed automation
              await triggerBookingAutomations(selectedBusiness.id, 'completed', bookingDataForAutomation);
              console.log('✅ Booking completed automation triggered successfully');
            } else {
              console.error('❌ Failed to get booking data for automation');
            }
          } catch (automationError) {
            console.error('❌ Failed to trigger booking completed automation:', automationError);
            // Don't throw - we don't want to break the main flow if automation fails
          }
        } else {
          console.log('🎯 Not final stage or no booking ID. isFinalStage:', isFinalStage, 'bookingId:', bookingId);
        }
      }

      if (selectedStageId) {
        await fetchStageEntries(selectedStageId);
      } else {
        await fetchAllStageEntries();
      }
      await fetchQueueEntries();
    } catch (err) {
      console.error('Error marking stage completed:', err);
    }
  };

  useEffect(() => {
    if (!selectedBusiness?.id) return;
    const channel = supabase
      .channel(`queue_entries_${selectedBusiness.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'queue_entries', filter: `business_id=eq.${selectedBusiness.id}` },
        () => {
          fetchQueueEntries(true); // Skip loading state for background updates
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedBusiness?.id]);

  useEffect(() => {
    if (!selectedBusiness?.id) return;
    const channel = supabase
      .channel(`queue_handoff_entries_${selectedBusiness.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'queue_handoff_entries', filter: `business_id=eq.${selectedBusiness.id}` },
        () => {
          if (selectedStageId) {
            fetchStageEntries(selectedStageId);
          } else {
            fetchAllStageEntries();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedBusiness?.id, selectedStageId]);

  useEffect(() => {
    if (!selectedBusiness?.id) return;
    const channel = supabase
      .channel(`queue_handoff_stages_${selectedBusiness.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'queue_handoff_stages', filter: `business_id=eq.${selectedBusiness.id}` },
        () => {
          fetchHandoffStages();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedBusiness?.id]);

  // Subscribe to bookings table changes for real-time status updates
  useEffect(() => {
    if (!selectedBusiness?.id) return;
    const channel = supabase
      .channel(`bookings_${selectedBusiness.id}`)
      .on(
        'postgres_changes',
        { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'bookings', 
          filter: `business_id=eq.${selectedBusiness.id}` 
        },
        (payload) => {
          // Refetch stage entries if booking is in handoff
          if (payload.new.in_que === true || payload.old?.in_que === true) {
            if (selectedStageId) {
              fetchStageEntries(selectedStageId, true); // Skip loading state
            } else {
              fetchAllStageEntries(true); // Skip loading state
            }
            fetchQueueEntries(true); // Skip loading state
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedBusiness?.id, selectedStageId]);

  // Subscribe to que_settings changes
  useEffect(() => {
    if (!selectedBusiness?.id) return;
    const channel = supabase
      .channel(`que_settings_${selectedBusiness.id}`)
      .on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'que_settings', 
          filter: `business_id=eq.${selectedBusiness.id}` 
        },
        async () => {
          // Settings changed, reprocess handoff
          try {
            await supabase.rpc('process_queue_handoff', {
              p_business_id: selectedBusiness.id
            });
            fetchQueueEntries(true); // Skip loading state
            if (selectedStageId) {
              fetchStageEntries(selectedStageId, true); // Skip loading state
            } else {
              fetchAllStageEntries(true); // Skip loading state
            }
          } catch (err) {
            console.error('Error processing handoff after settings change:', err);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedBusiness?.id, selectedStageId]);

  // Enhanced fallback polling - checks queue AND stages for stuck bookings
  useEffect(() => {
    if (!selectedBusiness?.id || activeTab !== 'que') return;

    let isProcessing = false;

    const enhancedFallbackCheck = async () => {
      if (isProcessing) return; // Prevent concurrent runs
      isProcessing = true;

      try {
        // Check if handoff is enabled
        const { data: settings } = await supabase
          .from('que_settings')
          .select('advance_to_handoff, handoff_completed_action')
          .eq('business_id', selectedBusiness.id)
          .single();

        if (!settings?.advance_to_handoff) {
          isProcessing = false;
          return;
        }

        // Check for waiting queue entries OR completed entries in stages
        const [queueCheck, stageCheck] = await Promise.all([
          supabase
            .from('queue_entries')
            .select('id')
            .eq('business_id', selectedBusiness.id)
            .eq('status', 'waiting')
            .limit(1),
          supabase
            .from('queue_handoff_entries')
            .select('id')
            .eq('business_id', selectedBusiness.id)
            .eq('status', 'completed')
            .limit(1)
        ]);

        // Process handoff if there's work to do
        if ((queueCheck.data && queueCheck.data.length > 0) || 
            (stageCheck.data && stageCheck.data.length > 0)) {
          await supabase.rpc('process_queue_handoff', {
            p_business_id: selectedBusiness.id
          });
          
          // Refresh UI - skip loading state for background updates
          fetchQueueEntries(true);
          if (selectedStageId) {
            fetchStageEntries(selectedStageId, true);
          } else {
            fetchAllStageEntries(true);
          }
        }
      } catch (err) {
        console.error('Enhanced fallback check failed:', err);
      } finally {
        isProcessing = false;
      }
    };

    // Run immediately on mount
    enhancedFallbackCheck();

    // Poll every 10 seconds as safety net
    const interval = setInterval(enhancedFallbackCheck, 10000);

    return () => clearInterval(interval);
  }, [selectedBusiness?.id, activeTab, selectedStageId]);

  // Tab visibility detection - pause polling when hidden
  useEffect(() => {
    if (!selectedBusiness?.id || activeTab !== 'que') return;

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Tab became visible - refresh everything (skip loading states to prevent scroll jump)
        fetchQueueEntries(true);
        fetchHandoffStages();
        if (selectedStageId) {
          fetchStageEntries(selectedStageId, true);
        } else {
          fetchAllStageEntries(true);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [selectedBusiness?.id, activeTab, selectedStageId]);

  const nextUpEntry = useMemo(() => queueEntries[0], [queueEntries]);
  const queueCount = queueEntries.length;

  // Filter tabs based on permissions
  const visibleTabs = useMemo(() => {
    return tabs.filter(tab => {
      if (tab.id === 'builder') {
        return permissions.queApp.viewBuilder;
      }
      return true;
    });
  }, [permissions.queApp.viewBuilder]);

  return (
    <div className="space-y-4">
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-6" aria-label="Que tabs">
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'que' && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Live Queue</h2>
              <p className="text-xs text-gray-500">Current bookings waiting in the queue.</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setIsAddModalOpen(true);
                  setIsAddModalAnimating(true);
                }}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-blue-600 px-3 py-1.5 rounded-md hover:bg-blue-700"
              >
                <Plus className="h-3 w-3" />
                Add
              </button>
              <button
                type="button"
                onClick={() => setIsReorderModalOpen(true)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-600 bg-amber-50 px-3 py-1.5 rounded-md hover:bg-amber-100"
              >
                <Edit2 className="h-3 w-3" />
                Edit
              </button>
              <button
                type="button"
                onClick={() => setIsQueueModalOpen(true)}
                className="text-xs font-semibold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-md hover:bg-blue-100"
              >
                View Queue
              </button>
              <button
                type="button"
                onClick={fetchQueueEntries}
                className="text-xs font-semibold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-md hover:bg-blue-100"
              >
                Refresh
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
              <div className="text-[11px] text-gray-500">People in queue</div>
              <div className="text-lg font-semibold text-gray-900 leading-tight">{queueCount}</div>
            </div>
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
              <div className="text-[11px] text-gray-500">Next up</div>
              <div className="text-sm font-semibold text-gray-900 leading-tight">
                {nextUpEntry
                  ? `${nextUpEntry.booking?.customer?.first_name || ''} ${nextUpEntry.booking?.customer?.last_name || ''}`.trim()
                  : 'No one in queue'}
              </div>
              {nextUpEntry?.booking?.services?.[0]?.name && (
                <div className="text-[11px] text-gray-500 mt-0.5">
                  {nextUpEntry.booking.services[0].name}
                </div>
              )}
            </div>
          </div>

          {isLoadingQueue && (
            <div className="text-sm text-gray-500">Loading queue...</div>
          )}

          {queueError && (
            <div className="text-sm text-red-600">{queueError}</div>
          )}

          {!isLoadingQueue && !queueError && queueEntries.length === 0 && (
            <div className="text-sm text-gray-500">
              {isQueueTableMissing
                ? 'Queue is empty.'
                : 'Queue is empty.'}
            </div>
          )}

          {!isLoadingQueue && !queueError && queueEntries.length > 0 && (
            <div className="text-sm text-gray-500">
              Queue details are available in the upcoming modal view.
            </div>
          )}

          <div className="mt-3 sm:mt-4">

            {isLoadingStages && (
              <div className="text-sm text-gray-500">Loading stages...</div>
            )}

            {!isLoadingStages && handoffStages.length === 0 && (
              <div className="text-sm text-gray-500">No stages configured yet.</div>
            )}

            {!isLoadingStages && handoffStages.length > 0 && (
              <div className="mb-3 sm:mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Stage
                </label>
                <select
                  value={selectedStageId || ''}
                  onChange={async (e) => {
                    const newStageId = e.target.value || null;
                    setSelectedStageId(newStageId);
                    hasInitializedStageEntriesRef.current = false;
                    hasInitializedAllStageEntriesRef.current = false;
                    
                    if (currentResource?.id && selectedBusiness?.id) {
                      try {
                        const { error } = await supabase
                          .from('queue_resource_defaults')
                          .upsert({
                            business_id: selectedBusiness.id,
                            resource_id: currentResource.id,
                            default_stage_id: newStageId
                          }, { onConflict: 'resource_id' });
                        
                        if (error) {
                          console.error('Error saving default stage:', error);
                        }
                      } catch (err) {
                        console.error('Error saving default stage:', err);
                      }
                    }
                  }}
                  className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">View all stages</option>
                  {handoffStages.map(stage => (
                    <option key={stage.id} value={stage.id}>
                      Stage {stage.stage_order}: {stage.title}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {(selectedStageId || selectedStageId === null) && (
              <div className="space-y-4">
                {isLoadingStageEntries && (
                  <div className="text-sm text-gray-500">Loading stage bookings...</div>
                )}

                {!isLoadingStageEntries && selectedStageId && stageEntries.length === 0 && (
                  <>
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500 text-center">
                      No bookings in this stage.
                    </div>
                    
                    {/* Pull from Queue Button - Only for Stage 1 when empty */}
                    {(() => {
                      const currentStage = handoffStages.find(stage => stage.id === selectedStageId);
                      if (currentStage?.stage_order !== 1) return null;
                      
                      const hasQueueEntries = queueEntries.length > 0;
                      const isDisabled = !hasQueueEntries || isPullingFromQueue;
                      
                      return (
                        <div className="mt-3 flex justify-center">
                          <button
                            onClick={handlePullFromQueue}
                            disabled={isDisabled}
                            className={`text-sm px-4 py-2 rounded-lg font-medium transition-colors ${
                              isDisabled
                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                            title={
                              !hasQueueEntries 
                                ? 'Queue is empty' 
                                : 'Pull next booking from queue'
                            }
                          >
                            {isPullingFromQueue ? 'Pulling...' : 'Pull from Queue'}
                          </button>
                        </div>
                      );
                    })()}
                  </>
                )}

                {!isLoadingStageEntries && selectedStageId && stageEntries.length > 0 && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-semibold text-gray-900">
                        Stage: {handoffStages.find(stage => stage.id === selectedStageId)?.title || 'Stage'}
                      </div>
                      <div className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                        Max: {handoffStages.find(stage => stage.id === selectedStageId)?.max_bookings || 1}
                      </div>
                      
                      {/* Pull from Queue Button - Only for Stage 1 */}
                      {(() => {
                        const currentStage = handoffStages.find(stage => stage.id === selectedStageId);
                        if (currentStage?.stage_order !== 1) return null;
                        
                        const currentCount = stageEntries.filter(e => e.status === 'active' || e.status === 'completed').length;
                        const isFull = currentCount >= (currentStage.max_bookings || 1);
                        const hasQueueEntries = queueEntries.length > 0;
                        const isDisabled = isFull || !hasQueueEntries || isPullingFromQueue;
                        
                        return (
                          <button
                            onClick={handlePullFromQueue}
                            disabled={isDisabled}
                            className={`text-xs px-3 py-1.5 rounded-md font-medium transition-colors ${
                              isDisabled
                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                            title={
                              isFull 
                                ? 'Stage is full' 
                                : !hasQueueEntries 
                                  ? 'Queue is empty' 
                                  : 'Pull next booking from queue'
                            }
                          >
                            {isPullingFromQueue ? 'Pulling...' : 'Pull from Queue'}
                          </button>
                        );
                      })()}
                    </div>
                    <div className="space-y-3 max-w-2xl mx-auto">
                      {stageEntries.map((entry) => {
                        const customer = entry.booking?.customer;
                        const service = entry.booking?.services?.[0];
                        const vehicle = service?.vehicle;
                        const bookingStatus = entry.booking?.status || 'scheduled';
                        
                        // Calculate payment status from payments
                        const totalAmount = entry.booking?.total_amount || 0;
                        const payments = entry.booking?.payments || [];
                        const totalPaid = payments
                          .filter((p: any) => p.status === 'completed')
                          .reduce((sum: number, p: any) => sum + (Number(p.amount) || 0), 0);
                        
                        let paymentStatus = 'unpaid';
                        if (totalAmount > 0) {
                          if (totalPaid === 0) {
                            paymentStatus = 'unpaid';
                          } else if (totalPaid >= totalAmount) {
                            paymentStatus = 'paid';
                          } else {
                            paymentStatus = 'partially_paid';
                          }
                        }
                        
                        const activeStage = handoffStages.find(stage => stage.id === selectedStageId);
                        const stageMode = activeStage?.handoff_mode;
                        const nextStage = handoffStages.find(
                          stage => stage.stage_order === ((activeStage?.stage_order || 0) + 1)
                        );
                        const showCompleteButton = stageMode === 'manual' || nextStage?.handoff_mode === 'manual';
                        const customerName = customer
                          ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
                          : 'Unknown customer';
                        const isNewFromQueue = newBookingIds.has(entry.booking_id);
                        const sourceStage = bookingSourceStage.get(entry.booking_id) || 'Queue';

                        return (
                          <div
                            key={entry.id}
                            className={`flex flex-col sm:flex-row sm:items-center justify-between rounded-lg border bg-white p-2.5 sm:p-4 ${getStatusBorderColor(bookingStatus)} relative`}
                          >
                            {/* Payment Status Badge - Top Right Corner */}
                            <div className="absolute top-2 right-2">
                              {paymentStatus === 'paid' ? (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800 border-2 border-green-300">
                                  ✓ PAID
                                </span>
                              ) : paymentStatus === 'partially_paid' ? (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800 border-2 border-yellow-300">
                                  ⚠ PARTIAL
                                </span>
                              ) : paymentStatus === 'refunded' ? (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-800 border-2 border-gray-300">
                                  ↩ REFUNDED
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 border-2 border-red-300">
                                  ✕ NOT PAID
                                </span>
                              )}
                            </div>
                            
                            {isNewFromQueue && (
                              <div className="absolute top-2 right-2 bg-green-500 text-white text-[10px] sm:text-xs font-bold px-2 py-0.5 rounded-full shadow-lg animate-pulse" style={{ marginTop: '32px' }}>
                                New from {sourceStage}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                                {customerName || 'Customer'}
                              </div>
                              
                              <div className="text-[11px] sm:text-xs text-gray-600 mt-0.5 sm:mt-1">
                                <span className="font-bold">{service?.name || 'Service'}</span>{service?.price ? ` • $${Number(service.price).toFixed(2)}` : ''}
                              </div>
                              {(vehicle?.year || vehicle?.make || vehicle?.model) && (
                                <div className="text-[11px] sm:text-xs mt-0.5 sm:mt-1">
                                  <span className="font-bold text-gray-700">Vehicle:</span> <span className="font-bold text-gray-900">{`${vehicle?.year || ''} ${vehicle?.make || ''} ${vehicle?.model || ''}${vehicle?.color ? ` (${vehicle.color})` : ''}`.trim()}</span>
                                </div>
                              )}
                              {entry.booking?.start_date && entry.booking?.start_time && (
                                <div className="text-[11px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
                                  Started: {new Date(entry.booking.start_date + 'T00:00:00').toLocaleDateString()} at {new Date(`2000-01-01T${entry.booking.start_time}`).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}
                                </div>
                              )}
                              {customer?.phone && (
                                <div className="text-[11px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
                                  {customer.phone}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2 sm:gap-3 text-[11px] sm:text-xs text-gray-500 mt-2 sm:mt-0 flex-shrink-0">
                              <span className="hidden sm:inline">{entry.status === 'completed' ? 'Completed' : 'In stage'}</span>
                              {showCompleteButton && (
                                <button
                                  type="button"
                                  onClick={() => handleMarkStageCompleted(entry.id)}
                                  disabled={entry.status === 'completed'}
                                  className="text-[11px] sm:text-xs font-semibold text-blue-600 bg-blue-50 px-2 sm:px-3 py-1 rounded-md hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-70 inline-flex items-center gap-1.5 sm:gap-2 whitespace-nowrap"
                                >
                                  {entry.status === 'completed' ? (
                                    <>
                                      <span className="h-2.5 w-2.5 sm:h-3 sm:w-3 rounded-full border-2 border-blue-600 border-t-transparent animate-spin" />
                                      Waiting
                                    </>
                                  ) : (
                                    'Complete Step'
                                  )}
                                </button>
                              )}
                              {/* Force Advance button for stuck completed entries */}
                              {entry.status === 'completed' && (
                                <button
                                  type="button"
                                  onClick={() => handleForceAdvance(entry.id)}
                                  className="text-[11px] sm:text-xs font-semibold text-amber-600 bg-amber-50 px-2 sm:px-3 py-1 rounded-md hover:bg-amber-100 whitespace-nowrap"
                                  title="Force advance to next stage"
                                >
                                  Force →
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {!isLoadingStageEntries && selectedStageId === null && (
                  <>
                    {handoffStages.map((stage) => {
                      const entriesForStage = stageEntries.filter((entry) => entry.stage?.id === stage.id);
                      return (
                        <div key={stage.id} className="rounded-lg border border-gray-200 bg-gray-50 p-2.5 sm:p-4 mb-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-xs sm:text-sm font-semibold text-gray-900">
                              Stage: {stage.title}
                            </div>
                            <div className="text-[10px] sm:text-xs text-gray-500 bg-gray-200 px-1.5 sm:px-2 py-0.5 rounded">
                              Max: {stage.max_bookings || 1}
                            </div>
                          </div>
                          {entriesForStage.length === 0 ? (
                            <div className="text-xs sm:text-sm text-gray-500 text-center">No bookings in this stage.</div>
                          ) : (
                            <div className="space-y-2 sm:space-y-3 max-w-2xl mx-auto">
                              {entriesForStage.map((entry) => {
                                const customer = entry.booking?.customer;
                                const service = entry.booking?.services?.[0];
                                const vehicle = service?.vehicle;
                                const bookingStatus = entry.booking?.status || 'scheduled';
                                
                                // Calculate payment status from payments
                                const totalAmount = entry.booking?.total_amount || 0;
                                const payments = entry.booking?.payments || [];
                                const totalPaid = payments
                                  .filter((p: any) => p.status === 'completed')
                                  .reduce((sum: number, p: any) => sum + (Number(p.amount) || 0), 0);
                                
                                let paymentStatus = 'unpaid';
                                if (totalAmount > 0) {
                                  if (totalPaid === 0) {
                                    paymentStatus = 'unpaid';
                                  } else if (totalPaid >= totalAmount) {
                                    paymentStatus = 'paid';
                                  } else {
                                    paymentStatus = 'partially_paid';
                                  }
                                }
                                
                                const stageMode = stage.handoff_mode;
                                const nextStage = handoffStages.find(
                                  s => s.stage_order === (stage.stage_order + 1)
                                );
                                const showCompleteButton = stageMode === 'manual' || nextStage?.handoff_mode === 'manual';
                                const customerName = customer
                                  ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
                                  : 'Unknown customer';
                                const isNewFromQueue = newBookingIds.has(entry.booking_id);
                                const sourceStage = bookingSourceStage.get(entry.booking_id) || 'Queue';

                                return (
                                  <div
                                    key={entry.id}
                                    className={`flex flex-col sm:flex-row sm:items-center justify-between rounded-lg border bg-white p-2.5 sm:p-4 ${getStatusBorderColor(bookingStatus)} relative`}
                                  >
                                    {/* Payment Status Badge - Top Right Corner */}
                                    <div className="absolute top-2 right-2">
                                      {paymentStatus === 'paid' ? (
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800 border-2 border-green-300">
                                          ✓ PAID
                                        </span>
                                      ) : paymentStatus === 'partially_paid' ? (
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800 border-2 border-yellow-300">
                                          ⚠ PARTIAL
                                        </span>
                                      ) : paymentStatus === 'refunded' ? (
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-800 border-2 border-gray-300">
                                          ↩ REFUNDED
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 border-2 border-red-300">
                                          ✕ NOT PAID
                                        </span>
                                      )}
                                    </div>
                                    
                                    {isNewFromQueue && (
                                      <div className="absolute top-2 right-2 bg-green-500 text-white text-[10px] sm:text-xs font-bold px-2 py-0.5 rounded-full shadow-lg animate-pulse" style={{ marginTop: '32px' }}>
                                        New from {sourceStage}
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <div className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                                        {customerName || 'Customer'}
                                      </div>
                                      <div className="text-[11px] sm:text-xs text-gray-600 mt-0.5 sm:mt-1">
                                        <span className="font-bold">{service?.name || 'Service'}</span>{service?.price ? ` • $${Number(service.price).toFixed(2)}` : ''}
                                      </div>
                                      {(vehicle?.year || vehicle?.make || vehicle?.model) && (
                                        <div className="text-[11px] sm:text-xs mt-0.5 sm:mt-1">
                                          <span className="font-bold text-gray-700">Vehicle:</span> <span className="font-bold text-gray-900">{`${vehicle?.year || ''} ${vehicle?.make || ''} ${vehicle?.model || ''}${vehicle?.color ? ` (${vehicle.color})` : ''}`.trim()}</span>
                                        </div>
                                      )}
                                      {entry.booking?.start_date && entry.booking?.start_time && (
                                        <div className="text-[11px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
                                          Started: {new Date(entry.booking.start_date + 'T00:00:00').toLocaleDateString()} at {new Date(`2000-01-01T${entry.booking.start_time}`).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}
                                        </div>
                                      )}
                                      {customer?.phone && (
                                        <div className="text-[11px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
                                          {customer.phone}
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2 sm:gap-3 text-[11px] sm:text-xs text-gray-500 mt-2 sm:mt-0 flex-shrink-0">
                                      <span className="hidden sm:inline">{entry.status === 'completed' ? 'Completed' : 'In stage'}</span>
                                        {showCompleteButton && (
                                          <button
                                            type="button"
                                            onClick={() => handleMarkStageCompleted(entry.id)}
                                            disabled={entry.status === 'completed'}
                                            className="text-[11px] sm:text-xs font-semibold text-blue-600 bg-blue-50 px-2 sm:px-3 py-1 rounded-md hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-70 inline-flex items-center gap-1.5 sm:gap-2 whitespace-nowrap"
                                          >
                                            {entry.status === 'completed' ? (
                                              <>
                                                <span className="h-2.5 w-2.5 sm:h-3 sm:w-3 rounded-full border-2 border-blue-600 border-t-transparent animate-spin" />
                                                Waiting
                                              </>
                                            ) : (
                                              'Complete Step'
                                            )}
                                          </button>
                                        )}
                                        {/* Force Advance button for stuck completed entries */}
                                        {entry.status === 'completed' && (
                                          <button
                                            type="button"
                                            onClick={() => handleForceAdvance(entry.id)}
                                            className="text-[11px] sm:text-xs font-semibold text-amber-600 bg-amber-50 px-2 sm:px-3 py-1 rounded-md hover:bg-amber-100 whitespace-nowrap"
                                            title="Force advance to next stage"
                                          >
                                            Force →
                                          </button>
                                        )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'builder' && <QueBuilder />}

      {/* Queue Modal */}
      {isQueueModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setIsQueueModalOpen(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900" id="modal-title">
                    Queue Overview
                  </h3>
                  <button
                    type="button"
                    onClick={() => setIsQueueModalOpen(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="mt-4">
                  {isLoadingQueue ? (
                    <div className="text-sm text-gray-500 text-center py-4">Loading queue...</div>
                  ) : queueError ? (
                    <div className="text-sm text-red-600 text-center py-4">{queueError}</div>
                  ) : queueEntries.length === 0 ? (
                    <div className="text-sm text-gray-500 text-center py-4">No vehicles in queue</div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {queueEntries.map((entry) => {
                        const customer = entry.booking?.customer;
                        const vehicle = entry.booking?.services?.[0]?.vehicle;
                        const customerName = customer
                          ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
                          : 'Unknown customer';
                        const vehicleInfo = vehicle
                          ? `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}${vehicle.color ? ` (${vehicle.color})` : ''}`.trim()
                          : 'No vehicle info';
                        
                        return (
                          <div
                            key={entry.id}
                            className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50"
                          >
                            <div className="flex items-center gap-4 flex-1">
                              <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                <span className="text-lg font-bold text-blue-600">{entry.queue_position}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-gray-900 truncate">
                                  {customerName}
                                </div>
                                <div className="text-xs text-gray-600 mt-0.5">
                                  <span className="font-medium">Vehicle:</span> {vehicleInfo}
                                </div>
                                {entry.booking?.services?.[0]?.name && (
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    Service: {entry.booking.services[0].name}
                                  </div>
                                )}
                                {customer?.phone && (
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    {customer.phone}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                              <div className="flex flex-col gap-1">
                                <button
                                  type="button"
                                  onClick={() => handleReorderQueue(entry.id, 'up')}
                                  disabled={isReordering || entry.queue_position === 1}
                                  className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                  title="Move up"
                                >
                                  <ChevronUp className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleReorderQueue(entry.id, 'down')}
                                  disabled={isReordering || entry.queue_position === queueEntries.length}
                                  className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                  title="Move down"
                                >
                                  <ChevronDown className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setIsQueueModalOpen(false)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add to Queue Modal - Right Side Mounted */}
      {(isAddModalOpen || isAddModalAnimating) && createPortal(
        <>
          <div 
            className={`fixed inset-0 transition-opacity duration-200 ${
              isAddModalOpen ? 'bg-black/50' : 'bg-black/0'
            }`}
            onClick={() => {
              setIsAddModalOpen(false);
            }}
            onTransitionEnd={handleAddModalTransitionEnd}
            style={{
              zIndex: 100200,
              paddingTop: typeof window !== 'undefined' && window.innerWidth < 640 ? 'env(safe-area-inset-top, 0px)' : '0',
              paddingBottom: typeof window !== 'undefined' && window.innerWidth < 640 ? 'env(safe-area-inset-bottom, 0px)' : '0'
            }}
          />
          {(() => {
            const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
            return (
              <div 
                className={`bg-white transform transition-all duration-200 ${
                  isMobile 
                    ? `relative w-full h-full ${isAddModalOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
                    : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                        isAddModalOpen ? 'translate-x-0' : 'translate-x-full'
                      }`
                }`}
                style={{ 
                  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
                  zIndex: 100210
                }}
                onClick={e => e.stopPropagation()}
              >
                <div className="h-full flex flex-col overflow-hidden">
                  {/* Close Button */}
                  <button
                    onClick={() => setIsAddModalOpen(false)}
                    className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <X className="h-5 w-5" />
                  </button>

                  {/* Header */}
                  <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
                    <h2 className="text-2xl font-bold text-gray-900" style={{ 
                      letterSpacing: '-0.02em'
                    }}>
                      Add Vehicle to Queue
                    </h2>
                  </div>

                  {/* Content */}
                  <div 
                    className="flex-1 overflow-y-auto bg-white px-6 py-6"
                    style={{ 
                      WebkitOverflowScrolling: 'touch',
                      paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
                    }}
                  >
                    <div className="space-y-4">
                  {/* Service Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Service*
                    </label>
                    {isLoadingCategories ? (
                      <div className="text-sm text-gray-500">Loading services...</div>
                    ) : categories.length > 0 ? (
                      <div className="space-y-3">
                        {categories.map((category) => (
                          <div key={category.id} className="border border-gray-200 rounded-lg p-3">
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedCategory(selectedCategory === category.id ? null : category.id);
                                setSelectedService(null);
                              }}
                              className={`w-full text-left font-medium text-sm ${
                                selectedCategory === category.id ? 'text-blue-600' : 'text-gray-700'
                              }`}
                            >
                              {category.category_title}
                            </button>
                            {selectedCategory === category.id && (
                              <div className="mt-2 space-y-2">
                                {(category.services || []).map((item: any, index: number) => (
                                  <button
                                    key={`${item.service?.id}-${item.priceOption?.id || index}`}
                                    type="button"
                                    onClick={() => setSelectedService(item)}
                                    className={`w-full text-left p-2 rounded border ${
                                      selectedService?.service?.id === item.service?.id &&
                                      selectedService?.priceOption?.id === item.priceOption?.id
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                  >
                                    <div className="font-medium text-sm">{item.service?.name || 'Service'}</div>
                                    {item.priceOption?.price && (
                                      <div className="text-xs text-gray-600">
                                        ${Number(item.priceOption.price).toFixed(2)}
                                        {item.priceOption?.duration_minutes && ` • ${item.priceOption.duration_minutes} mins`}
                                      </div>
                                    )}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">No services available. Please configure services in Que Builder.</div>
                    )}
                  </div>

                  {/* Customer Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Customer*
                    </label>
                    {selectedCustomer ? (
                      <div className="flex items-center justify-between p-3 border border-gray-300 rounded-md bg-gray-50">
                        <div>
                          <div className="font-medium text-sm">
                            {selectedCustomer.first_name} {selectedCustomer.last_name}
                          </div>
                          <div className="text-xs text-gray-600 mt-0.5">
                            {selectedCustomer.email && <span>{selectedCustomer.email}</span>}
                            {selectedCustomer.email && selectedCustomer.phone && <span className="mx-1">•</span>}
                            {selectedCustomer.phone && <span>{selectedCustomer.phone}</span>}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setIsSelectCustomerModalOpen(true)}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                        >
                          Change
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setIsSelectCustomerModalOpen(true)}
                        className="w-full p-3 border-2 border-dashed border-gray-300 rounded-md text-sm text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors"
                      >
                        + Select Customer
                      </button>
                    )}
                  </div>

                  {/* Vehicle Information */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Vehicle Year*
                      </label>
                      <input
                        type="text"
                        value={vehicleYear}
                        onChange={(e) => setVehicleYear(e.target.value)}
                        className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                        placeholder="Year"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Vehicle Make*
                      </label>
                      <input
                        type="text"
                        value={vehicleMake}
                        onChange={(e) => setVehicleMake(e.target.value)}
                        className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                        placeholder="Make"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Vehicle Model*
                      </label>
                      <input
                        type="text"
                        value={vehicleModel}
                        onChange={(e) => setVehicleModel(e.target.value)}
                        className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                        placeholder="Model"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Vehicle Color*
                      </label>
                      <input
                        type="text"
                        value={vehicleColor}
                        onChange={(e) => setVehicleColor(e.target.value)}
                        className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                        placeholder="Color"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes <span className="text-gray-500">(optional)</span>
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={2}
                      className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Additional notes"
                    />
                  </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex flex-row-reverse gap-3">
                    <button
                      type="button"
                      onClick={handleAddToQueue}
                      disabled={isSubmitting || !selectedService || !selectedCustomer || !vehicleYear || !vehicleMake || !vehicleModel || !vehicleColor}
                      className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isSubmitting ? 'Adding...' : 'Add to Queue'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsAddModalOpen(false)}
                      className="px-4 py-2 bg-white text-gray-700 text-sm font-semibold rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
        </>,
        document.body
      )}

      {/* Select Customer Modal */}
      {createPortal(
        <SelectCustomerModal
          isOpen={isSelectCustomerModalOpen}
          onClose={() => setIsSelectCustomerModalOpen(false)}
          onSelect={(customer) => {
            setSelectedCustomer(customer);
            setIsSelectCustomerModalOpen(false);
          }}
          baseZIndex={100300}
        />,
        document.body
      )}

      {/* Queue Reorder Modal */}
      <QueueReorderModal
        isOpen={isReorderModalOpen}
        onClose={() => setIsReorderModalOpen(false)}
        onSave={() => {
          // Refresh all data after reorder
          fetchQueueEntries();
          if (selectedStageId) {
            fetchStageEntries(selectedStageId);
          } else {
            fetchAllStageEntries();
          }
        }}
      />
    </div>
  );
}

